import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  catchError,
  Observable,
  Subject,
  takeUntil,
  throwError,
} from 'rxjs';
import { ConfigService } from './core/config.service';
import { Adherent } from '../interfaces/BDD-Ref/adherent';

@Injectable({
  providedIn: 'root',
})
export class UserSessionService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private readonly selectedAdherentSubject: BehaviorSubject<Adherent | null> =
    new BehaviorSubject<Adherent | null>(null);
  selectedAdherent$: Observable<Adherent | null> =
    this.selectedAdherentSubject.asObservable();

  private activiteSubject = new BehaviorSubject<any>(null);
  activite$ = this.activiteSubject.asObservable();

  private filialeSubject = new BehaviorSubject<string | null>(null);

  filiale$ = this.filialeSubject.asObservable();

  private static adherents: Adherent[] = [];

  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
  ) {}

  /**
   * Unsubscribes from all subscriptions to prevent memory leaks when the service is destroyed.
   */
  ngOnDestroy() {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Retrieves officine data for a given user and stores the adherents statically.
   * @param email The user's email.
   * @param type The page type (for logging purposes).
   * @returns Observable<Adherent[]>
   */
  public getOfficine(email: string, type: string): Observable<Adherent[]> {
    return this.http
      .get<
        Adherent[]
      >(`${this.configService.config.backendUri}/gestion-comptes/officines?email=${encodeURIComponent(email)}`)
      .pipe(
        takeUntil(this._destroying$),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  /**
   * Stores the adherents for static retrieval.
   * Should be called once the adherents are retrieved.
   * @param adherents The list of adherents available for the user.
   */
  static setAdherents(adherents: Adherent[]): void {
    UserSessionService.adherents = adherents;
  }

  /**
   * Retrieves the list of idAdherent available for the connected user.
   * @returns number[]
   */
  static getAvailableAdherentIds(): number[] {
    return UserSessionService.adherents.map((adherent) => adherent.idAdherent);
  }

  /**
   * Sets the currently selected adherent.
   * @param adherent The adherent to be selected.
   */
  setSelectedAdherent(adherent: Adherent | null): void {
    this.selectedAdherentSubject.next(adherent);
  }

  /**
   * Gets the currently selected adherent as an observable.
   * @returns Observable<Adherent | null>
   */
  getSelectedAdherent(): Observable<Adherent | null> {
    return this.selectedAdherent$;
  }

  setActivite(value: string) {
    this.activiteSubject.next(value);
  }

  getActivite(): string {
    return this.activiteSubject.value;
  }

  setFiliale(filiale: string | null): void {
    this.filialeSubject.next(filiale);
  }
}
